import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft, Star, StarOff, Eye, EyeOff, GripVertical,
  Calendar, MapPin, Filter, Search, ChevronUp, ChevronDown,
  Layout as LayoutIcon, Sparkles, TrendingUp
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { getImageUrl } from '../../utils/apiUrl';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const CATEGORIES = [
  { value: 'music', label: 'Music' },
  { value: 'comedy', label: 'Comedy' },
  { value: 'sports', label: 'Sports' },
  { value: 'nightlife', label: 'Nightlife' },
  { value: 'festivals', label: 'Festivals' },
  { value: 'awards', label: 'Awards' },
  { value: 'arts', label: 'Arts' },
  { value: 'food', label: 'Food & Drink' },
  { value: 'business', label: 'Business' },
  { value: 'wedding', label: 'Wedding' },
];

export default function LandingPageControl() {
  const [events, setEvents] = useState([]);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [settings, setSettings] = useState({
    show_nearby_events: true,
    show_trending: true,
    categories_order: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [eventsRes, featuredRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/events/all`),
        axios.get(`${API_URL}/api/admin/events/featured`),
        axios.get(`${API_URL}/api/admin/landing-page/settings`)
      ]);
      
      setEvents(eventsRes.data);
      setFeaturedEvents(featuredRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleFeatured = async (eventId, currentStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/events/${eventId}/feature`,
        { 
          is_featured: !currentStatus,
          featured_order: featuredEvents.length + 1
        },
        
      );
      toast.success(currentStatus ? 'Event removed from featured' : 'Event added to featured');
      fetchData();
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const toggleHidden = async (eventId, currentStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/events/${eventId}/ranking`,
        { is_hidden: !currentStatus },
        
      );
      toast.success(currentStatus ? 'Event now visible' : 'Event hidden from landing page');
      fetchData();
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/landing-page/settings`,
        newSettings,
        
      );
      setSettings(newSettings);
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const moveEvent = async (eventId, direction) => {
    const currentIndex = featuredEvents.findIndex(e => e.event_id === eventId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= featuredEvents.length) return;
    
    const newOrder = [...featuredEvents];
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
    
    const eventOrders = newOrder.map((e, idx) => ({
      event_id: e.event_id,
      order: idx + 1
    }));
    
    try {
      await axios.put(
        `${API_URL}/api/admin/events/reorder-featured`,
        eventOrders,
        
      );
      fetchData();
    } catch (error) {
      toast.error('Failed to reorder events');
    }
  };

  const filteredEvents = events.filter(event => {
    const matchesCategory = selectedCategory === 'all' || event.category === selectedCategory;
    const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         event.venue?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <LayoutIcon className="w-8 h-8" />
              Landing Page Control
            </h1>
            <p className="text-muted-foreground">Manage featured events and landing page content</p>
          </div>
        </div>

        <Tabs defaultValue="featured" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="featured" data-testid="featured-tab">
              <Sparkles className="w-4 h-4 mr-2" />
              Featured
            </TabsTrigger>
            <TabsTrigger value="all-events" data-testid="all-events-tab">
              <Calendar className="w-4 h-4 mr-2" />
              All Events
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">
              <Filter className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Featured Events Tab */}
          <TabsContent value="featured">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  Featured Events
                </CardTitle>
                <CardDescription>
                  These events will appear prominently on the landing page. Drag to reorder.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-20 skeleton rounded-lg" />
                    ))}
                  </div>
                ) : featuredEvents.length > 0 ? (
                  <div className="space-y-3">
                    {featuredEvents.map((event, index) => (
                      <div
                        key={event.event_id}
                        className="flex items-center gap-4 p-4 rounded-xl border bg-gradient-to-r from-yellow-500/5 to-transparent"
                        data-testid={`featured-event-${event.event_id}`}
                      >
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => moveEvent(event.event_id, 'up')}
                            disabled={index === 0}
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => moveEvent(event.event_id, 'down')}
                            disabled={index === featuredEvents.length - 1}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center text-yellow-500 font-bold">
                          {index + 1}
                        </div>
                        
                        {event.image_url && (
                          <img
                            src={getImageUrl(event.image_url)}
                            alt={event.title}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold truncate">{event.title}</h4>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {format(new Date(event.date), 'MMM d, yyyy')}
                            </span>
                            <Badge variant="outline">{event.category}</Badge>
                          </div>
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleFeatured(event.event_id, true)}
                          data-testid={`unfeature-${event.event_id}`}
                        >
                          <StarOff className="w-4 h-4 mr-2" />
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No featured events</h3>
                    <p className="text-muted-foreground">
                      Go to "All Events" tab to feature events on the landing page.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Events Tab */}
          <TabsContent value="all-events">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle>All Approved Events</CardTitle>
                    <CardDescription>
                      Feature events or hide them from the landing page
                    </CardDescription>
                  </div>
                  <div className="flex gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="Search events..."
                        className="pl-10 pr-4 py-2 rounded-lg border bg-background"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-16 skeleton rounded-lg" />
                    ))}
                  </div>
                ) : filteredEvents.length > 0 ? (
                  <div className="space-y-2">
                    {filteredEvents.map((event) => (
                      <div
                        key={event.event_id}
                        className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                          event.is_hidden ? 'opacity-50 bg-muted/30' : 'hover:bg-muted/50'
                        } ${event.is_featured ? 'border-yellow-500/50 bg-yellow-500/5' : ''}`}
                        data-testid={`event-${event.event_id}`}
                      >
                        {event.image_url && (
                          <img
                            src={getImageUrl(event.image_url)}
                            alt={event.title}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold truncate">{event.title}</h4>
                            {event.is_featured && (
                              <Badge className="bg-yellow-500 text-black">
                                <Star className="w-3 h-3 mr-1" />
                                Featured
                              </Badge>
                            )}
                            {event.is_hidden && (
                              <Badge variant="secondary">
                                <EyeOff className="w-3 h-3 mr-1" />
                                Hidden
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span>{format(new Date(event.date), 'MMM d, yyyy')}</span>
                            <Badge variant="outline" className="text-xs">{event.category}</Badge>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.city || event.venue}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            variant={event.is_featured ? "default" : "outline"}
                            size="sm"
                            onClick={() => toggleFeatured(event.event_id, event.is_featured)}
                            className={event.is_featured ? "bg-yellow-500 hover:bg-yellow-600 text-black" : ""}
                          >
                            {event.is_featured ? (
                              <><StarOff className="w-4 h-4 mr-1" /> Unfeature</>
                            ) : (
                              <><Star className="w-4 h-4 mr-1" /> Feature</>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleHidden(event.event_id, event.is_hidden)}
                          >
                            {event.is_hidden ? (
                              <><Eye className="w-4 h-4 mr-1" /> Show</>
                            ) : (
                              <><EyeOff className="w-4 h-4 mr-1" /> Hide</>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No events found</h3>
                    <p className="text-muted-foreground">
                      {searchQuery || selectedCategory !== 'all' 
                        ? 'Try adjusting your filters'
                        : 'No approved events available'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Landing Page Settings</CardTitle>
                <CardDescription>
                  Control what sections appear on the landing page
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-primary" />
                    <div>
                      <Label className="text-base">Show Nearby Events</Label>
                      <p className="text-sm text-muted-foreground">
                        Display events based on user's location
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.show_nearby_events}
                    onCheckedChange={(checked) => updateSettings({...settings, show_nearby_events: checked})}
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    <div>
                      <Label className="text-base">Show Trending Section</Label>
                      <p className="text-sm text-muted-foreground">
                        Display trending/popular events
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.show_trending}
                    onCheckedChange={(checked) => updateSettings({...settings, show_trending: checked})}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
